import { useEffect, useMemo } from "react";
import { Link } from "wouter";
import { ArrowUpRight, Award, BookOpen, ClipboardCheck, Folder, Sparkles, TrendingUp, Users } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { activityFeed, candidates, expedientes as expSeed } from "@/data";
import { useDemoStore } from "@/store/useDemoStore";
import { useEstandares } from "@/lib/api/estandares";

export default function AdminDashboard() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("admin"), [setRole]);

  // ── Datos reales del backend ──
  const { data: backendStandards = [] } = useEstandares({});

  // Adapter: mapeo a la forma que usa el render (codigo/titulo/sector/elementos/criterios/candidatosActivos/certificadosEmitidos)
  const standards = useMemo(
    () => backendStandards.map((e) => ({
      codigo: e.codigo,
      titulo: e.nombre,
      sector: e.sector ?? "—",
      estado: e.estado === "publicado" ? "publicado" : e.estado === "revision" ? "en-revision" : "borrador",
      elementos: e.elementos_count ?? 0,
      criterios: e.criterios_count ?? 0,
      candidatosActivos: e.candidatos_activos ?? 0,
      certificadosEmitidos: e.certificados_emitidos ?? 0,
    })),
    [backendStandards],
  );

  const totalCertificados = useMemo(
    () => backendStandards.reduce((a, e) => a + (e.certificados_emitidos ?? 0), 0),
    [backendStandards],
  );
  const totalCandidatos = useMemo(
    () => backendStandards.reduce((a, e) => a + (e.candidatos_activos ?? 0), 0),
    [backendStandards],
  );
  const publicados = standards.filter((s) => s.estado === "publicado").length;

  const kpis = [
    { label: "Estándares publicados", value: publicados, delta: `${standards.length} en total`, icon: BookOpen, tone: "var(--cc-navy)" },
    { label: "Candidatos activos", value: totalCandidatos, delta: "datos reales", icon: Users, tone: "var(--cc-teal)" },
    { label: "Evaluaciones en curso", value: expSeed.filter((e) => e.examenTeorico.aplicado && !e.certificado.emitido).length, delta: "mock (Sprint 2)", icon: ClipboardCheck, tone: "#0891b2" },
    { label: "Certificados emitidos", value: totalCertificados, delta: "histórico", icon: Award, tone: "#16a34a" },
  ];

  return (
    <AppLayout pageTitle="Dashboard ejecutivo">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 22 }}>
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Card key={k.label} testId={`kpi-${k.label}`}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: k.tone, color: "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon size={17} />
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 3, color: "var(--cc-green)", fontSize: 11, fontWeight: 600 }}>
                  <TrendingUp size={12} /> {k.delta}
                </div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: "var(--cc-navy)", lineHeight: 1, marginBottom: 4 }}>{k.value}</div>
              <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>{k.label}</div>
            </Card>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <Card>
          <SectionTitle action={<Link href="/admin/estandares" style={{ fontSize: 12, color: "var(--cc-teal)", fontWeight: 600, textDecoration: "none" }}>Ver todos →</Link>}>
            Estándares en operación
          </SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {standards.map((s) => (
              <Link
                key={s.codigo}
                href={`/admin/estandares/${s.codigo}`}
                data-testid={`row-standard-${s.codigo}`}
                style={{
                  display: "grid", gridTemplateColumns: "auto 1fr auto auto auto", gap: 14,
                  alignItems: "center", padding: 12, borderRadius: 10,
                  border: "1px solid var(--cc-gray-100)", textDecoration: "none",
                }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--cc-navy)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                  {s.codigo}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-gray-800)", marginBottom: 2 }}>{s.titulo}</div>
                  <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{s.sector} · {s.elementos} elementos · {s.criterios} criterios</div>
                </div>
                <Badge tone="navy">{s.candidatosActivos} candidatos</Badge>
                <Badge tone={s.estado === "publicado" ? "green" : s.estado === "en-revision" ? "amber" : "gray"}>
                  {s.estado === "publicado" ? "Publicado" : s.estado === "en-revision" ? "En revisión" : "Borrador"}
                </Badge>
                <ArrowUpRight size={15} color="var(--cc-gray-400)" />
              </Link>
            ))}
          </div>
        </Card>

        <Card>
          <SectionTitle>Actividad reciente</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {activityFeed.map((a) => (
              <div key={a.id} style={{ display: "flex", gap: 10 }} data-testid={`activity-${a.id}`}>
                <div style={{
                  width: 8, height: 8, marginTop: 6, borderRadius: "50%", flexShrink: 0,
                  background: a.tipo === "certificado" ? "var(--cc-green)" : a.tipo === "ia" ? "var(--cc-teal)" : a.tipo === "dictamen" ? "var(--cc-navy)" : "var(--cc-gray-400)",
                }} />
                <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--cc-gray-700)" }}>
                  <span style={{ fontWeight: 600 }}>{a.actor}</span> {a.accion}{" "}
                  <span style={{ fontWeight: 600, color: "var(--cc-navy)" }}>{a.objeto}</span>
                  <div style={{ fontSize: 11, color: "var(--cc-gray-400)", marginTop: 2 }}>{a.hora}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16 }}>
        <Link href="/admin/ia" data-testid="link-quick-ia" style={{ textDecoration: "none" }}>
          <Card style={{ cursor: "pointer", background: "var(--cc-teal-bg)", border: "1px solid #a5f3fc" }}>
            <Sparkles size={20} color="var(--cc-teal)" />
            <div style={{ marginTop: 10, fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>Pregúntale a CertiAI</div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-600)", marginTop: 4 }}>Métricas, alertas y estado del sistema en chat.</div>
          </Card>
        </Link>
        <Link href="/admin/grupos" data-testid="link-quick-grupos" style={{ textDecoration: "none" }}>
          <Card style={{ cursor: "pointer" }}>
            <Users size={20} color="var(--cc-teal)" />
            <div style={{ marginTop: 10, fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>Grupos y generaciones</div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-500)", marginTop: 4 }}>2 generaciones activas · 12 inscripciones</div>
          </Card>
        </Link>
        <Link href="/admin/estandares/EC1781/publicar" data-testid="link-quick-publicar" style={{ textDecoration: "none" }}>
          <Card style={{ cursor: "pointer" }}>
            <BookOpen size={20} color="var(--cc-navy)" />
            <div style={{ marginTop: 10, fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>Publicación EC1781</div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-500)", marginTop: 4 }}>Revisar versión 1.2 lista para CONOCER</div>
          </Card>
        </Link>
        <Link href="/admin/grupos/grp-2026-1" data-testid="link-quick-expedientes" style={{ textDecoration: "none" }}>
          <Card style={{ cursor: "pointer" }}>
            <Folder size={20} color="#0891b2" />
            <div style={{ marginTop: 10, fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>Expedientes activos</div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-500)", marginTop: 4 }}>4 listos para dictamen</div>
          </Card>
        </Link>
      </div>
    </AppLayout>
  );
}
